import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, SkipBack, SkipForward, Volume2, Clock, Calendar, ArrowRight, Headphones } from "lucide-react";
import { podcastMeta, episodes as mockEpisodes } from "../mock/mock";
import { getEpisodes } from "../lib/api";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function Podcast() {
  const [episodes, setEpisodes] = useState(mockEpisodes);
  const [current, setCurrent] = useState(null); // episode object
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const timer = useRef(null);
  const TOTAL = 100; // simulated seconds for demo

  useEffect(() => {
    getEpisodes()
      .then((data) => { if (Array.isArray(data) && data.length) setEpisodes(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (playing) {
      timer.current = setInterval(() => {
        setProgress((p) => {
          if (p >= 1) { setPlaying(false); return 1; }
          return p + 1 / TOTAL;
        });
      }, 300);
    }
    return () => clearInterval(timer.current);
  }, [playing]);

  const selectEpisode = (ep) => {
    if (current?.id === ep.id) { setPlaying(!playing); return; }
    setCurrent(ep);
    setProgress(0);
    setPlaying(true);
  };

  const idx = current ? episodes.findIndex((e) => e.id === current.id) : -1;
  const skip = (dir) => {
    if (idx < 0) return;
    const next = (idx + dir + episodes.length) % episodes.length;
    selectEpisode(episodes[next]);
  };

  return (
    <div className="pb-28">
      {/* Hero */}
      <section className="bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-5 py-16 grid md:grid-cols-[300px_1fr] gap-10 items-center">
          <div className="rounded-2xl overflow-hidden shadow-2xl aspect-square">
            <img src={podcastMeta.cover} alt={podcastMeta.title} className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="font-ui tracking-[0.25em] text-teal text-sm font-semibold uppercase flex items-center gap-2">
              <Headphones size={16} /> Podcast · 85+ Episodes
            </p>
            <h1 className="font-serif-display text-5xl md:text-6xl font-bold mt-3">{podcastMeta.title}</h1>
            <p className="font-ui text-white/80 mt-5 text-lg leading-relaxed max-w-xl">{podcastMeta.description}</p>
            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={() => selectEpisode(episodes[0])} className="inline-flex items-center gap-2 bg-teal text-navy px-7 py-3.5 rounded-full font-ui font-semibold hover:bg-cream transition-colors">
                <Play size={18} fill="currentColor" /> Play Latest
              </button>
              {podcastMeta.subscribeLinks.map((l) => (
                <a key={l.name} href={l.url} className="inline-flex items-center border border-white/35 px-5 py-3.5 rounded-full font-ui font-medium hover:bg-white/10 transition-colors text-sm">
                  {l.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Episode list */}
      <section className="max-w-5xl mx-auto px-5 py-14">
        <h2 className="font-serif-display text-3xl font-bold text-navy mb-8">All Episodes</h2>
        <div className="space-y-4">
          {episodes.map((ep) => {
            const active = current?.id === ep.id;
            return (
              <div key={ep.id} className={`rounded-2xl border transition-all bg-white ${active ? "ring-2 ring-teal shadow-lg" : "hover:shadow-md"}`} style={{ borderColor: "var(--cream-dark)" }}>
                <div className="p-4 md:p-5 flex gap-4 md:gap-5 items-center">
                  <button
                    onClick={() => selectEpisode(ep)}
                    className="relative shrink-0 w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden group"
                  >
                    <img src={ep.thumb} alt={ep.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-navy/45 flex items-center justify-center group-hover:bg-navy/65 transition-colors">
                      {active && playing
                        ? <Pause size={24} className="text-white" fill="white" />
                        : <Play size={24} className="text-white" fill="white" />}
                    </div>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-ui text-xs font-semibold text-teal">EP {ep.number}</span>
                      <span className="font-ui text-xs px-2 py-0.5 rounded-full bg-cream-dark text-navy/70">{ep.pillar}</span>
                    </div>
                    <Link to={`/podcast/${ep.id}`} className="block">
                      <h3 className="font-serif-display text-xl md:text-2xl font-semibold text-navy mt-1 hover:text-orange transition-colors truncate">{ep.title}</h3>
                    </Link>
                    <p className="font-ui text-sm text-navy/60 mt-1 truncate">with {ep.guest} · {ep.guestRole}</p>
                    <div className="flex items-center gap-4 mt-2 text-navy/50 font-ui text-xs">
                      <span className="flex items-center gap-1"><Calendar size={13} /> {ep.date}</span>
                      <span className="flex items-center gap-1"><Clock size={13} /> {ep.duration}</span>
                    </div>
                  </div>
                  <Link to={`/podcast/${ep.id}`} className="hidden md:inline-flex items-center gap-1 text-teal font-ui text-sm font-semibold hover:gap-2 transition-all shrink-0">
                    Show notes <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Spotify-style sticky player */}
      {current && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#181818] text-white border-t border-white/10 shadow-[0_-8px_30px_-10px_rgba(0,0,0,0.5)]">
          <div className="h-1 w-full bg-white/15">
            <div className="h-full bg-teal transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
            <img src={current.thumb} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
            <div className="min-w-0 flex-1 md:flex-none md:w-56">
              <p className="font-ui text-sm font-semibold truncate">{current.title}</p>
              <p className="font-ui text-xs text-white/60 truncate">EP {current.number} · {current.guest}</p>
            </div>
            <div className="flex items-center gap-4 flex-1 justify-center">
              <button onClick={() => skip(-1)} className="text-white/70 hover:text-white transition-colors"><SkipBack size={20} fill="currentColor" /></button>
              <button onClick={() => setPlaying(!playing)} className="w-11 h-11 rounded-full bg-teal text-navy flex items-center justify-center hover:scale-105 transition-transform">
                {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" />}
              </button>
              <button onClick={() => skip(1)} className="text-white/70 hover:text-white transition-colors"><SkipForward size={20} fill="currentColor" /></button>
            </div>
            <div className="hidden md:flex items-center gap-2 w-56 justify-end">
              <span className="font-ui text-xs text-white/60 tabular-nums">{fmt(progress * TOTAL)} / {fmt(TOTAL)}</span>
              <Volume2 size={18} className="text-white/60" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
